// @ts-strict-ignore
import * as React from 'react';
import Modal from 'antd/lib/modal';
import Form, { useForm, useWatch } from 'antd/lib/form/Form';
import FormItem from 'antd/lib/form/FormItem';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import Select from 'antd/lib/select';
import Spin from 'antd/lib/spin';
import DatePicker from 'antd/lib/date-picker';
import Divider from 'antd/lib/divider';
import Radio from 'antd/lib/radio';
import Checkbox from 'antd/lib/checkbox';
import Alert from 'antd/lib/alert';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { Link, useHistory, useParams } from 'react-router-dom';
import { flatMap } from 'lodash';

import * as API from '../../api';
import { AppComponentProps, AppRouteComponentMatchParams, Nullable } from '../../typescriptHelpers';
import { RootState } from '../../reducers/reducers';
import {
    CreateEditReleaseType,
    CreateHotfixOrRollbackReleaseRequest,
    DestinationOptions,
    DestinationOptionsAction,
    NewReleaseRequest,
    UpdateReleaseRequest,
} from '../../actions/release';
import { DestinationCounter } from '../appHome/DestinationCounter';
import { GetPossibleBaseTagsRequest, GetPossibleSourceVersionsForRollbackRequest } from '../../actions/app';
import {
    selectAppPlatform,
    selectAppSettings,
    selectAppStoreType,
    selectAppTimeFormatSetting,
    selectAuthCredentials,
    selectDestinationInfoByDestinationStepConfigId,
    selectEffectiveTimezone,
    selectIntegrationForCapability,
    selectSchedulingIntegrationForPilotRotation,
    selectIsDemoInstanceOrDemoModeEnabled,
    selectKickedOffAt,
    selectIsPlayReleaseLifecycleEnabled,
    selectOrgUser,
    selectRelease,
    selectReleasePilotEligibleUsersForApp,
    selectVCSSupportsDirectPushCheck,
    selectWorkingBranch,
} from '../../selectors/selectors';
import {
    appStoreSupportsRollbacks,
    canConfigureTargetReleaseDate,
    getIsGooglePlayStore,
    isAppPlatform,
    releaseTypeToCreateEditReleaseType,
} from '../../helpers';
import {
    AllRollbackResigningStrategies,
    AutomationType,
    HotfixCheckboxTypes,
    IntegrationCapability,
    IntegrationType,
    PatternToken,
    ReleaseScheduleCadenceStyle,
    ReleaseTag,
    ReleaseType,
    RollbackResigningStrategy,
    SettingType,
    SubscriptionFeatureType,
} from '../../types';
import { globalMessage } from '../../utils/globalMessage';
import { createAppRoute, createAppSettingsRouteWithSettingType } from '../../helpers/routeHelpers';
import { formatDateWithTimezone } from '../../timeHelpers';
import { dayjs } from '@/extendedDayjs';
import { displayUserNameLong } from '../../helpers/displayHelpers';
import { displayTextForResigningStrategy } from '@/helpers/displayHelpers';
import { FROM_ROTATION } from '../../constants';
import PendingPilotSwapAlert from '../PendingPilotSwapAlert';
import { isValidCommitHashFormat } from '@/helpers/formHelpers';
import * as Storage from '../../helpers/localStorageHelper';

import { useVersionValidation } from '../../utils/hooks/useVersionValidation';
import { usePredictVersion } from '../../utils/hooks/usePredictVersion';
import { useReleaseCreation } from '../../utils/hooks/useReleaseCreation';
import { useAppSettings } from '@/utils/hooks/helpers';

import { ReleaseVersionFormItem } from './ReleaseVersionFormItem';
import { ReleaseNameFormItem } from './ReleaseNameFormItem';
import { MatchPhasedReleaseCheckbox } from './MatchPhasedReleaseCheckbox';
import { PlanRestrictedFeatureWrapper } from '../planRestrictions/PlanRestrictedFeatureWrapper';
import IntegrationIcon from '../icons/IntegrationIcon';
import { CommitSelector } from '../CommitSelectorComponent';
import { BetaBadge } from '../automations/AppAutomationSettings';
import { antGrey, runwayColors, titleStyle } from '../styles/globalStyles';
import TimeWithConditionalTz from '@/components/common/TimeWithConditionalTz';
import BranchCreationCheck from '../vcs/BranchCreationCheck';

export type PrepareReleaseMode = 'standard' | 'hotfix';

export interface PrepareNewWorkflowRunModalProps extends AppComponentProps {
    open: boolean;
    onCancel: () => void;
    onSuccess?: () => void;

    mode: PrepareReleaseMode;

    isEditingRelease?: boolean;
    releaseId?: string;

    isConvertingRollback?: boolean;
    releaseVersion?: string;
    releaseName?: string;

    displayOnlyTargetDates?: boolean;
    canBeSubmitted?: Nullable<boolean>;
    onEditTargetDates?: () => void;
}

export const PrepareNewWorkflowRunModal: React.FC<PrepareNewWorkflowRunModalProps> = (props) => {
    const history = useHistory();
    const { orgIdParam, appIdParam } = useParams<AppRouteComponentMatchParams>();
    const [form] = useForm();

    const initialReleaseType = props.mode === 'hotfix' ? CreateEditReleaseType.Hotfix : CreateEditReleaseType.Standard;

    const [formValid, setFormValid] = React.useState(true);
    const [selectedReleaseType, setSelectedReleaseType] = React.useState<CreateEditReleaseType>(initialReleaseType);
    const [predictedPilotId, setPredictedPilotId] = React.useState<string | null>(null);
    const [showOverridePilotAlert, setShowOverridePilotAlert] = React.useState(false);
    const [showPendingPilotSwapAlert, setShowPendingPilotSwapAlert] = React.useState(false);
    const [shouldMatchPreviousPhasedRelease, setShouldMatchPreviousPhasedRelease] = React.useState(false);

    const [branchHelp, setBranchHelp] = React.useState('');
    const [branchName, setBranchName] = React.useState('');
    const [bumpVersionHelp, setBumpVersionHelp] = React.useState('');
    const [possibleBasesForHotfix, setPossibleBasesForHotfix] = React.useState<ReleaseTag[]>([]);
    const [basesLoading, setBasesLoading] = React.useState(false);
    const [baseForHotfix, setBaseForHotfix] = React.useState<ReleaseTag | null>(null);
    const [predictedHotfixBase, setPredictedHotfixBase] = React.useState<string | null>(null);
    const [possibleSourceVersionsForRollback, setPossibleSourceVersionsForRollback] = React.useState<string[]>([]);

    const [commits, setCommits] = React.useState([]);
    const [fetchCommitsError, setFetchCommitsError] = React.useState<string | null>(null);
    const [commitsLoading, setCommitsLoading] = React.useState(false);
    const [commitSearchValue, setCommitSearchValue] = React.useState('');
    const latestCommitsRequest = React.useRef(0);
    const selectedCommits = useWatch('commitHashes', form);
    const shouldCreateBranch = useWatch('shouldCreateBranch', form);

    const authCredentials = useSelector((state: RootState) => selectAuthCredentials(state));
    const appPlatform = useSelector((state: RootState) => selectAppPlatform(state, props.appId));
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, props.appId));
    const pilotUsers = useSelector((state: RootState) => selectReleasePilotEligibleUsersForApp(state, props.appId));
    const use24HourTimeFormat = useSelector((state: RootState) => selectAppTimeFormatSetting(state, props.appId));
    const effectiveTimezone = useSelector((state: RootState) => selectEffectiveTimezone(state, props.appId));
    const isDemoMode = useSelector((state: RootState) => selectIsDemoInstanceOrDemoModeEnabled(state, props.orgId));
    const workingBranch = useSelector((state: RootState) => selectWorkingBranch(state, props.appId));
    const isPlayReleaseLifecycleEnabled = useSelector((state: RootState) =>
        selectIsPlayReleaseLifecycleEnabled(state, props.orgId),
    );

    const release = useSelector((state: RootState) => {
        if (props.releaseId && props.isEditingRelease) {
            return selectRelease(state, props.releaseId);
        }
        return null;
    });

    const kickedOffAt = useSelector((state: RootState) => {
        if (props.isEditingRelease && props.releaseId) {
            return selectKickedOffAt(state, props.releaseId);
        }
        return null;
    });

    const scheduleIntegration = useSelector(selectSchedulingIntegrationForPilotRotation(props.appId));

    const predictedPilotUser = useSelector((state: RootState) => selectOrgUser(state, props.orgId, predictedPilotId));

    const vcsIntegration = useSelector((state: RootState) =>
        selectIntegrationForCapability(state, props.appId, IntegrationCapability.VersionControl),
    );
    const vcsSupportsDirectPushCheck = useSelector(selectVCSSupportsDirectPushCheck(props.appId, props.orgId));

    const appSettings = useAppSettings(props);
    const scheduleSettings = appSettings.releaseScheduleCadenceSettings;
    const scheduleIsActive =
        !!scheduleSettings && scheduleSettings.scheduleCadence !== ReleaseScheduleCadenceStyle.Never;

    const releaseBranchPatterns = useSelector((state: RootState) => {
        const settings = selectAppSettings(state, props.appId);
        return settings.releaseBranchPatterns.map((p) => p.pattern);
    });

    const destinationInfoByStepConfigId = useSelector((state: RootState) =>
        selectDestinationInfoByDestinationStepConfigId(state, props.appId),
    );
    const allDestinations = React.useMemo(
        () => Object.values(destinationInfoByStepConfigId),
        [destinationInfoByStepConfigId],
    );
    const hasMultipleDestinations = allDestinations.length > 1;

    const [prepareForAllDestinations, setPrepareForAllDestinations] = React.useState(true);

    const {
        validateVersion: doValidateVersion,
        resultIsStaticBranch,
        versionStringForPossibleBump,
        isReleasePilotFromRotationScheduleIntegration,
        latestCompletedReleaseVersion,
    } = useVersionValidation({
        orgId: props.orgId,
        appId: props.appId,
        releaseId: props.releaseId,
        releaseType: selectedReleaseType,
    });

    const { predictVersion } = usePredictVersion({
        orgId: props.orgId,
        appId: props.appId,
    });

    const {
        isLoading,
        createReleaseSuccess,
        createReleaseError,
        updateReleaseSuccess,
        updateReleaseError,
        createNewRelease,
        createNewReleaseWithConversion,
        updateRelease,
    } = useReleaseCreation({
        orgId: props.orgId,
        appId: props.appId,
        releaseId: props.releaseId,
    });

    const isGooglePlayStore = getIsGooglePlayStore(appStoreType);
    const canConfigureReleaseDate = canConfigureTargetReleaseDate(
        appStoreType,
        appPlatform,
        isPlayReleaseLifecycleEnabled,
    );
    const isHotfixMode = props.mode === 'hotfix';
    const isHotfixSelected = selectedReleaseType === CreateEditReleaseType.Hotfix;
    const isRollbackSelected = selectedReleaseType === CreateEditReleaseType.Rollback;
    const showRollbackOption = isHotfixMode && !props.isConvertingRollback && appStoreSupportsRollbacks(appStoreType);
    const showCommitSelector =
        vcsIntegration?.id === IntegrationType.GitHubVCS || vcsIntegration?.id === IntegrationType.GitLabVCS;
    const shouldFetchData = !isDemoMode;

    // Creating the release branch is what the VCS rejects, so only apps that will get one have
    // anything to check
    const showBranchCreationCheck =
        vcsSupportsDirectPushCheck && !resultIsStaticBranch && !props.isEditingRelease && !props.displayOnlyTargetDates;

    const renderBranchCreationCheck = (description: string, disabledReason?: string) => (
        <BranchCreationCheck
            orgId={props.orgId}
            appId={props.appId}
            branch={branchName}
            description={description}
            muted
            disabledReason={
                disabledReason ??
                (branchName ? undefined : 'Enter a valid version first, so Runway knows which branch to check.')
            }
        />
    );

    const shouldShowMatchPhasedReleaseOption =
        (isHotfixMode && isHotfixSelected && isGooglePlayStore) ||
        (props.isEditingRelease && release?.type === ReleaseType.Hotfix && isGooglePlayStore);

    const foundAnyBranchPatternsUsingReleaseNameToken = releaseBranchPatterns.some((pattern) =>
        pattern.includes(PatternToken.ReleaseName),
    );
    const disableReleaseVersion =
        props.isEditingRelease && release ? release.doesBranchExist && !release.isStaticReleaseBranch : false;
    const disableEditReleaseName = foundAnyBranchPatternsUsingReleaseNameToken && disableReleaseVersion;

    const predictedPilotDisplayName = predictedPilotUser ? displayUserNameLong(predictedPilotUser) : null;
    const currentPilotIsRotationPilot = release?.releasePilotId ? release.releasePilotId === predictedPilotId : false;
    const scheduleIntegrationIcon =
        scheduleIntegration ? <IntegrationIcon integrationId={scheduleIntegration.id} /> : null;

    const getTitle = (): string => {
        if (props.displayOnlyTargetDates) return 'Edit target dates';
        if (props.isConvertingRollback) return 'Convert rollback to roll-forward hotfix';
        if (props.isEditingRelease) return 'Edit release settings';
        return isHotfixMode ? 'Prepare a hotfix' : 'Prepare a new release';
    };

    const getButtonTitle = (): string => {
        if (props.isConvertingRollback && props.releaseId) {
            return `Convert ${props.releaseVersion} to roll-forward hotfix`;
        }
        if (props.isEditingRelease && release) return 'Save';
        return isHotfixMode ? 'Prepare hotfix' : 'Prepare release';
    };

    const validateForm = () => {
        const errors = flatMap(form.getFieldsError().map((field) => field.errors));
        setFormValid(errors.length === 0);
    };

    const handleVersionValidation = async (version: string): Promise<void> => {
        const values = form.getFieldsValue();
        const result = await doValidateVersion(version, values.sourceVersion);

        setPredictedPilotId(result.releasePilotFromRotation);

        if (result.hotfixBase) {
            setPredictedHotfixBase(result.hotfixBase);
        }
        if (result.branchName) {
            setBranchName(result.branchName);
        }
        setPossibleBasesForHotfix(result.previousTags ?? []);
        setBasesLoading(false);

        if (!result.hotfixPreviousVersion && isHotfixSelected) {
            throw new Error('No previous completed release to prepare hotfix from');
        }
    };

    const setBranchHelpDefaults = () => {
        setBranchHelp('Immediately create release branch from previous release tag');
    };

    const setBumpHelpDefaults = () => {
        setBumpVersionHelp('Bump version on release branch');
    };

    const baseTagSelected = (selectedTag: string) => {
        const releaseTag = possibleBasesForHotfix.find((v) => v.name === selectedTag);
        if (releaseTag) {
            setBaseForHotfix(releaseTag);
            getCandidateCherryPickCommits(releaseTag.version);
            form.validateFields(['baseForHotfix']);
        }
    };

    const getCandidateCherryPickCommits = React.useCallback(
        (baseTag?: string) => {
            // A base change can leave an earlier request in flight - only the newest one may write
            const requestId = ++latestCommitsRequest.current;
            setCommitsLoading(true);
            setFetchCommitsError(null);
            API.getCandidateSelectableCommits({ appId: props.appId, orgId: props.orgId, baseTag }, authCredentials)
                .then((response) => {
                    if (requestId !== latestCommitsRequest.current) {
                        return;
                    }
                    setCommits(response.commits);
                    setCommitsLoading(false);
                })
                .catch((error) => {
                    if (requestId !== latestCommitsRequest.current) {
                        return;
                    }
                    setCommitsLoading(false);
                    setFetchCommitsError(error.message);
                });
        },
        [authCredentials, props.appId, props.orgId],
    );

    const searchForCommit = React.useCallback(
        async (commitHash: string) => {
            setCommitsLoading(true);
            try {
                const response = await API.getCandidateSelectableCommits(
                    { appId: props.appId, orgId: props.orgId, commitHash },
                    authCredentials,
                );
                if (response.commits.length === 0) return;
                const commitFound = response.commits[0];
                if (commits.find((c) => c.hash === commitFound.hash)) return;
                setCommits([...commits, commitFound]);
            } catch (error) {
                setFetchCommitsError(error.message);
            } finally {
                setCommitsLoading(false);
            }
        },
        [authCredentials, commits, props.appId, props.orgId],
    );

    const populatePossibleTagsForHotfix = React.useCallback(() => {
        const request: GetPossibleBaseTagsRequest = { orgId: props.orgId, appId: props.appId };
        setBasesLoading(true);
        return API.getPossibleBaseTags(request, authCredentials)
            .then((sourceTags) => sourceTags.length > 0 && setPossibleBasesForHotfix(sourceTags))
            .catch(() => undefined)
            .finally(() => setBasesLoading(false));
    }, [authCredentials, props.orgId, props.appId]);

    const getPossibleSourceVersionsForRollback = React.useCallback(() => {
        const request: GetPossibleSourceVersionsForRollbackRequest = { orgId: props.orgId, appId: props.appId };
        return API.getPossibleSourceVersions(request, authCredentials)
            .then((sourceVersions) => {
                setPossibleSourceVersionsForRollback(sourceVersions);
                if (sourceVersions.length > 0) {
                    form.setFieldValue('sourceVersion', sourceVersions[0]);
                }
            })
            .catch(() => setPossibleSourceVersionsForRollback([]));
    }, [authCredentials, props.orgId, props.appId, form]);

    const submitForm = () => {
        const values = form.getFieldsValue();

        const destinationIdsFromForm: string[] = values.destinationStepConfigIds ?? [];
        const destinationActionFromForm: DestinationOptionsAction = values.destinationAction ?? 'include';

        const computedDestinationOptions: DestinationOptions | undefined =
            !prepareForAllDestinations && destinationIdsFromForm.length > 0 ?
                { destinationIds: destinationIdsFromForm, action: destinationActionFromForm }
            :   undefined;

        if (props.isEditingRelease && release) {
            const request: UpdateReleaseRequest = {
                orgId: props.orgId,
                appId: props.appId,
                releaseId: props.releaseId!,
                releaseType: releaseTypeToCreateEditReleaseType(release.type),
                version: values.releaseVersion,
                releasePilotId: values.releasePilotId,
                targetKickoffDate: values.targetKickoffDate?.startOf('minute').toISOString() ?? null,
                targetSubmitDate: values.targetSubmitDate?.startOf('minute').toISOString() ?? null,
                targetReleaseDate: values.targetReleaseDate?.startOf('minute').toISOString() ?? null,
                releaseName: values.releaseName,
                shouldMatchPreviousPhasedRelease: values.shouldMatchPreviousPhasedRelease,
            };
            updateRelease(request);
        } else if (isHotfixMode) {
            if (values) {
                const localStorageData = {
                    shouldCreateBranch: values[HotfixCheckboxTypes.ShouldCreateBranch],
                    shouldBumpVersionOnBranch: values[HotfixCheckboxTypes.ShouldBumpVersionOnBranch],
                };
                Storage.setItem(appIdParam, localStorageData);
            }

            const request: CreateHotfixOrRollbackReleaseRequest = {
                orgId: props.orgId,
                appId: props.appId,
                releaseType: selectedReleaseType,
                releasePilotId: values.releasePilotId,
                shouldCreateBranch: values.shouldCreateBranch ?? false,
                shouldBumpVersionOnBranch: values.shouldBumpVersionOnBranch ?? false,
                version: values.releaseVersion,
                releaseName: values.releaseName,
                commitsToCherryPick: values.commitHashes ?? [],
                sourceVersion: values.sourceVersion,
                base: baseForHotfix ?? undefined,
                resigningStrategy: values.resigningStrategy,
                shouldMatchPreviousPhasedRelease,
                destinationOptions: computedDestinationOptions,
            };

            if (props.isConvertingRollback) {
                createNewReleaseWithConversion(request, true, props.releaseId);
            } else {
                createNewRelease(request);
            }
        } else {
            const request: NewReleaseRequest = {
                orgId: props.orgId,
                appId: props.appId,
                releaseType: CreateEditReleaseType.Standard,
                version: values.releaseVersion,
                releasePilotId: values.releasePilotId,
                targetKickoffDate: values.targetKickoffDate?.startOf('hour').toISOString() ?? undefined,
                targetSubmitDate: values.targetSubmitDate?.startOf('hour').toISOString() ?? undefined,
                targetReleaseDate: values.targetReleaseDate?.startOf('hour').toISOString() ?? undefined,
                releaseName: values.releaseName,
                destinationOptions: computedDestinationOptions,
            };
            createNewRelease(request);
        }
    };

    const handleSelectPilotChange = (value: string) => {
        if (value !== FROM_ROTATION && currentPilotIsRotationPilot) {
            setShowOverridePilotAlert(true);
        } else {
            setShowOverridePilotAlert(false);
        }
        setShowPendingPilotSwapAlert(value !== FROM_ROTATION);
    };

    React.useEffect(() => {
        if (isValidCommitHashFormat(commitSearchValue)) {
            searchForCommit(commitSearchValue);
        }
    }, [commitSearchValue, searchForCommit]);

    React.useEffect(() => {
        setCommitSearchValue('');
    }, [selectedCommits]);

    React.useEffect(() => {
        if (branchName && baseForHotfix) {
            setBranchHelp(`Immediately create branch '${branchName}' from tag ${baseForHotfix.name}`);
        } else {
            setBranchHelpDefaults();
        }
        if (versionStringForPossibleBump) {
            setBumpVersionHelp(`Bump version on branch '${branchName}' to ${versionStringForPossibleBump}`);
        } else {
            setBumpHelpDefaults();
        }
    }, [branchName, versionStringForPossibleBump, baseForHotfix]);

    React.useEffect(() => {
        if (possibleBasesForHotfix?.length > 0) {
            if (predictedHotfixBase) {
                const predictedTag = possibleBasesForHotfix.find((tag) => tag.name === predictedHotfixBase);
                if (predictedTag && !baseForHotfix) {
                    setBaseForHotfix(predictedTag);
                    getCandidateCherryPickCommits(predictedTag.version);
                    return;
                }
            }
            if (!baseForHotfix) {
                setBaseForHotfix(possibleBasesForHotfix[0]);
                getCandidateCherryPickCommits(possibleBasesForHotfix[0].version);
            }
        }
    }, [possibleBasesForHotfix, predictedHotfixBase, baseForHotfix]);

    React.useEffect(() => {
        if (baseForHotfix) {
            form.setFieldValue('baseForHotfix', baseForHotfix.name);
        }
    }, [baseForHotfix, form]);

    React.useEffect(() => {
        if (props.open) {
            setSelectedReleaseType(initialReleaseType);
            setPredictedHotfixBase(null);
            setBaseForHotfix(null);
            setPossibleBasesForHotfix([]);
            setCommits([]);

            const willFetchBases = isHotfixMode && shouldFetchData && !props.isEditingRelease;
            setBasesLoading(willFetchBases);
            setCommitsLoading(willFetchBases && showCommitSelector);

            const hotfixStoredValues = Storage.getItem(appIdParam);
            const initialValues = {
                releaseVersion: null,
                releasePilotId: null,
                targetKickoffDate: null,
                targetSubmitDate: null,
                targetReleaseDate: null,
                releaseName: null,
                sourceVersion: null,
                resigningStrategy: RollbackResigningStrategy.LocalResign,
                shouldMatchPreviousPhasedRelease: null,
                shouldCreateBranch: hotfixStoredValues?.[HotfixCheckboxTypes.ShouldCreateBranch] ?? true,
                shouldBumpVersionOnBranch: hotfixStoredValues?.[HotfixCheckboxTypes.ShouldBumpVersionOnBranch] ?? true,
                destinationAction: 'include',
                destinationStepConfigIds: [],
                baseForHotfix: null,
            };

            if (props.isEditingRelease && release) {
                form.setFieldsValue({
                    ...initialValues,
                    releaseVersion: release.version,
                    targetKickoffDate:
                        release.targetKickoffDate ? dayjs(release.targetKickoffDate).tz(effectiveTimezone) : null,
                    targetSubmitDate:
                        release.targetSubmitDate ? dayjs(release.targetSubmitDate).tz(effectiveTimezone) : null,
                    targetReleaseDate:
                        release.targetReleaseDate ? dayjs(release.targetReleaseDate).tz(effectiveTimezone) : null,
                    releaseName: release.releaseName,
                    releasePilotId: release.releasePilotId,
                    shouldMatchPreviousPhasedRelease: release.shouldMatchPreviousPhasedRelease,
                });
            } else if (props.isConvertingRollback && props.releaseVersion) {
                form.setFieldsValue({
                    ...initialValues,
                    releaseVersion: props.releaseVersion,
                    releaseName: props.releaseName,
                });
                form.validateFields(['releaseVersion', 'releaseName']);
            } else {
                form.setFieldsValue(initialValues);

                if (shouldFetchData) {
                    predictVersion(isHotfixMode).then((version) => {
                        form.setFieldValue('releaseVersion', version);
                        form.validateFields(['releaseVersion', 'releaseName']);
                    });

                    if (isHotfixMode) {
                        populatePossibleTagsForHotfix();
                        if (showRollbackOption) {
                            getPossibleSourceVersionsForRollback();
                        }
                    }
                }
            }

            if (isHotfixMode) {
                setBranchHelpDefaults();
                setBumpHelpDefaults();
            }
        }
    }, [props.open, props.isEditingRelease, props.isConvertingRollback, isHotfixMode]);

    React.useEffect(() => {
        if (createReleaseError && props.open) {
            globalMessage.error(createReleaseError);
        }
    }, [createReleaseError, props.open]);

    React.useEffect(() => {
        if (createReleaseSuccess && props.open) {
            const successMsg =
                isHotfixMode ?
                    isHotfixSelected ? 'New roll-forward hotfix created!'
                    :   'New rollback hotfix created!'
                :   'New release created.';
            globalMessage.success(successMsg);

            props.onSuccess?.();
            props.onCancel();

            if (props.isConvertingRollback && props.releaseId) {
                Storage.clearLastSelectedReleaseStepForRelease(props.releaseId);
                history.replace(createAppRoute(orgIdParam, appIdParam));
            }
        }
    }, [createReleaseSuccess, props.open, isHotfixMode, isHotfixSelected]);

    React.useEffect(() => {
        if (updateReleaseError && props.isEditingRelease && props.open) {
            globalMessage.error(updateReleaseError);
        }
    }, [updateReleaseError, props.isEditingRelease, props.open]);

    React.useEffect(() => {
        if (updateReleaseSuccess && props.isEditingRelease && props.open) {
            const newVersion = form.getFieldValue('releaseVersion');
            const previousVersion = release?.version;
            const versionChanged = newVersion && newVersion !== previousVersion;

            if (versionChanged) {
                globalMessage.success('Release version and settings updated!');
                history.replace(createAppRoute(orgIdParam, appIdParam));
            } else {
                globalMessage.success('Release settings updated!');
                props.onSuccess?.();
                props.onEditTargetDates?.();
            }
        }
    }, [updateReleaseSuccess, props.isEditingRelease, props.open]);

    // Falls back to the predicted base: the tag list can come back without a match for it
    const previousTag = baseForHotfix?.name ?? predictedHotfixBase ?? '';
    const actualKickoffDate = kickedOffAt ? dayjs(kickedOffAt) : null;
    const actualSubmitDate = release?.submittedAt ? dayjs(release.submittedAt) : null;
    const actualReleasedDate = release?.releasedAt ? dayjs(release.releasedAt) : null;

    return (
        <Modal
            open={props.open}
            title={getTitle()}
            okText={getButtonTitle()}
            okButtonProps={{ disabled: !formValid, loading: isLoading }}
            onOk={() => form.submit()}
            confirmLoading={isLoading}
            onCancel={props.onCancel}
            afterClose={() => setShowOverridePilotAlert(false)}
            width={650}
        >
            <Form
                form={form}
                initialValues={{
                    destinationAction: 'include',
                    destinationStepConfigIds: [],
                }}
                onFieldsChange={validateForm}
                onFinish={submitForm}
            >
                {props.isConvertingRollback && (
                    <div style={{ marginBottom: 20 }}>
                        <Text strong>
                            Converting {props.releaseVersion} to a roll-forward hotfix will discard this rollback.
                        </Text>
                    </div>
                )}

                <ReleaseVersionFormItem
                    disabled={disableReleaseVersion}
                    hidden={props.displayOnlyTargetDates}
                    helpText={
                        disableReleaseVersion ?
                            'You cannot change the version or hotfix selection if a release branch has already been created for this version.'
                        :   null
                    }
                    onValidate={handleVersionValidation}
                />

                {!isHotfixMode && showBranchCreationCheck && (
                    <FormItem>
                        {renderBranchCreationCheck(
                            branchName ?
                                `Runway will create branch '${branchName}' when this release is kicked off.`
                            :   'Runway will create the release branch when this release is kicked off.',
                        )}
                    </FormItem>
                )}

                <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.ReleasePilots}>
                    {(isDisabled) => (
                        <>
                            <FormItem
                                label={
                                    <PlanRestrictedFeatureWrapper
                                        feature={SubscriptionFeatureType.ReleasePilots}
                                        requiresTooltip
                                    >
                                        <Text>Release pilot</Text>
                                    </PlanRestrictedFeatureWrapper>
                                }
                                name="releasePilotId"
                                hidden={props.displayOnlyTargetDates}
                                labelCol={{ flex: '130px' }}
                                help={
                                    isReleasePilotFromRotationScheduleIntegration ?
                                        `Your schedule is managed externally. Create an override on ${
                                            scheduleIntegration?.name ??
                                            'your incident management & scheduling integration'
                                        } to edit the pilot for this release.`
                                    :   null
                                }
                            >
                                <Select
                                    placeholder="Select"
                                    allowClear
                                    disabled={isDisabled || isReleasePilotFromRotationScheduleIntegration}
                                    onChange={handleSelectPilotChange}
                                >
                                    <Select.Option value={FROM_ROTATION}>
                                        <Space align="center">
                                            {isReleasePilotFromRotationScheduleIntegration && scheduleIntegrationIcon}
                                            <Text>
                                                From rotation{' '}
                                                {predictedPilotDisplayName && (
                                                    <Text type="secondary">({predictedPilotDisplayName})</Text>
                                                )}
                                            </Text>
                                        </Space>
                                    </Select.Option>
                                    {pilotUsers.map((user) => (
                                        <Select.Option key={user.id} value={user.id}>
                                            <Space align="center">
                                                {isReleasePilotFromRotationScheduleIntegration &&
                                                    scheduleIntegrationIcon}
                                                <Text>{displayUserNameLong(user)}</Text>
                                            </Space>
                                        </Select.Option>
                                    ))}
                                </Select>
                            </FormItem>
                            {showOverridePilotAlert && (
                                <Alert
                                    style={{ marginTop: 10, marginBottom: 16 }}
                                    type="warning"
                                    description={
                                        <>
                                            This will replace the existing pilot{' '}
                                            {predictedPilotDisplayName && (
                                                <>
                                                    (<b>{predictedPilotDisplayName}</b>){' '}
                                                </>
                                            )}
                                            from your rotation for this release. The rotation will continue for your
                                            next release. Visit{' '}
                                            <Link
                                                to={createAppSettingsRouteWithSettingType(
                                                    orgIdParam,
                                                    appIdParam,
                                                    SettingType.ReleasePilotRotation,
                                                )}
                                            >
                                                App settings {'>'} Release pilot rotation
                                            </Link>{' '}
                                            to manage your rotation.
                                        </>
                                    }
                                />
                            )}
                            {showPendingPilotSwapAlert && (
                                <PendingPilotSwapAlert orgId={props.orgId} appId={props.appId} />
                            )}
                        </>
                    )}
                </PlanRestrictedFeatureWrapper>

                <ReleaseNameFormItem disabled={disableEditReleaseName} hidden={props.displayOnlyTargetDates} />

                {!props.displayOnlyTargetDates && !props.isEditingRelease && hasMultipleDestinations && (
                    <>
                        <Divider />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            <Checkbox
                                checked={prepareForAllDestinations}
                                onChange={(e) => {
                                    setPrepareForAllDestinations(e.target.checked);
                                    if (e.target.checked) {
                                        form.setFieldValue('destinationStepConfigIds', []);
                                    }
                                }}
                            >
                                Prepare {isHotfixMode ? 'hotfix' : 'release'} for all destinations in this workflow
                            </Checkbox>

                            {!prepareForAllDestinations && (
                                <>
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 8,
                                            flexWrap: 'wrap',
                                        }}
                                    >
                                        <FormItem name="destinationAction" noStyle>
                                            <Select
                                                style={{ width: 100 }}
                                                options={[
                                                    { value: 'include', label: 'Include' },
                                                    { value: 'exclude', label: 'Exclude' },
                                                ]}
                                            />
                                        </FormItem>
                                        <Text>selected destinations:</Text>
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        <FormItem
                                            name="destinationStepConfigIds"
                                            dependencies={['destinationAction']}
                                            style={{ marginBottom: 0 }}
                                            rules={[
                                                {
                                                    validator: async (_, value: string[] | undefined) => {
                                                        if (!hasMultipleDestinations) return;
                                                        if (prepareForAllDestinations) return;

                                                        const v = value ?? [];
                                                        const action =
                                                            (form.getFieldValue('destinationAction') as
                                                                | DestinationOptionsAction
                                                                | undefined) ?? 'include';

                                                        if (action === 'include' && v.length === 0) {
                                                            throw new Error(
                                                                'Select at least one destination to include.',
                                                            );
                                                        }

                                                        if (
                                                            action === 'exclude' &&
                                                            v.length === allDestinations.length
                                                        ) {
                                                            throw new Error(
                                                                'You cannot exclude all destinations. Leave at least one destination unselected.',
                                                            );
                                                        }
                                                    },
                                                },
                                            ]}
                                        >
                                            <Select
                                                mode="multiple"
                                                style={{ minWidth: 200, flex: 1 }}
                                                placeholder="Select destinations"
                                                optionLabelProp="label"
                                                maxTagCount={'responsive'}
                                                tagRender={(props) => {
                                                    const { value, closable, onClose } = props;
                                                    const destination = allDestinations.find(
                                                        (d) => d.stepConfigId === value,
                                                    );
                                                    if (!destination) return <span>{value}</span>;
                                                    return (
                                                        <span
                                                            style={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: 4,
                                                                backgroundColor: antGrey.grey3,
                                                                borderRadius: 4,
                                                                padding: '2px 4px',
                                                                marginRight: 4,
                                                                border: `1px solid ${antGrey.grey5}`,
                                                            }}
                                                        >
                                                            <DestinationCounter
                                                                destinations={[destination]}
                                                                size={18}
                                                            />

                                                            <span>{destination.name || destination.appName}</span>
                                                            {closable && (
                                                                <span
                                                                    onMouseDown={(e) => {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                    }}
                                                                    onClick={onClose}
                                                                    style={{
                                                                        cursor: 'pointer',
                                                                        marginLeft: 2,
                                                                        color: antGrey.grey7,
                                                                    }}
                                                                >
                                                                    ×
                                                                </span>
                                                            )}
                                                        </span>
                                                    );
                                                }}
                                            >
                                                {allDestinations.map((destination) => (
                                                    <Select.Option
                                                        key={destination.stepConfigId}
                                                        value={destination.stepConfigId}
                                                        label={destination.name || destination.appName}
                                                    >
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                            <DestinationCounter
                                                                destinations={[destination]}
                                                                size={20}
                                                            />
                                                            <span>{destination.name || destination.appName}</span>
                                                        </div>
                                                    </Select.Option>
                                                ))}
                                            </Select>
                                        </FormItem>
                                    </div>
                                </>
                            )}
                        </div>
                    </>
                )}

                {!props.displayOnlyTargetDates && <Divider />}

                {isHotfixMode && !props.isEditingRelease && (
                    <Radio.Group
                        onChange={(e) => setSelectedReleaseType(e.target.value)}
                        value={selectedReleaseType}
                        style={{ width: '100%' }}
                    >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                            <Radio value={CreateEditReleaseType.Hotfix}>
                                Create a hotfix release by{' '}
                                {resultIsStaticBranch ? 'fixing forward' : 'branching off a previous tag'}
                            </Radio>
                            {isHotfixSelected && (
                                <div style={{ marginLeft: 20, display: 'flex', flexDirection: 'column' }}>
                                    {!resultIsStaticBranch && (
                                        <FormItem
                                            label="Base"
                                            name="baseForHotfix"
                                            validateTrigger="onSubmit"
                                            rules={[
                                                {
                                                    required: true,
                                                    message: 'You must pick a valid base tag to prepare hotfix',
                                                },
                                            ]}
                                        >
                                            <Select
                                                options={possibleBasesForHotfix?.map((releaseTag) => ({
                                                    label:
                                                        releaseTag.version === latestCompletedReleaseVersion ?
                                                            `${releaseTag.name} (latest release)`
                                                        :   releaseTag.name,
                                                    value: releaseTag.name,
                                                }))}
                                                onChange={baseTagSelected}
                                                loading={basesLoading}
                                                notFoundContent={basesLoading ? <Spin size="small" /> : undefined}
                                                placeholder={basesLoading ? 'Loading tags...' : 'Select a base'}
                                            />
                                        </FormItem>
                                    )}
                                    {!resultIsStaticBranch && (
                                        <FormItem
                                            name="shouldCreateBranch"
                                            valuePropName="checked"
                                            extra={
                                                showBranchCreationCheck ?
                                                    renderBranchCreationCheck(
                                                        branchHelp,
                                                        shouldCreateBranch ? undefined : 'Enable Create branch first.',
                                                    )
                                                :   branchHelp
                                            }
                                        >
                                            <Checkbox>Create branch</Checkbox>
                                        </FormItem>
                                    )}
                                    <FormItem
                                        name="shouldBumpVersionOnBranch"
                                        valuePropName="checked"
                                        extra={bumpVersionHelp}
                                        rules={[
                                            {
                                                validator: (_, shouldBumpVersion) => {
                                                    if (shouldBumpVersion) {
                                                        const createBranch = form.getFieldValue('shouldCreateBranch');
                                                        if (!createBranch && !resultIsStaticBranch) {
                                                            return Promise.reject(
                                                                "Cannot automatically bump version if 'Create branch' is not also enabled",
                                                            );
                                                        }
                                                    }
                                                    return Promise.resolve();
                                                },
                                            },
                                        ]}
                                        dependencies={['shouldCreateBranch']}
                                    >
                                        <Checkbox>Bump version</Checkbox>
                                    </FormItem>
                                    {showCommitSelector && (
                                        <PlanRestrictedFeatureWrapper
                                            automationType={AutomationType.AutoCherryPickFromWorkingBranch}
                                        >
                                            {(isDisabled) => (
                                                <FormItem
                                                    label={
                                                        <PlanRestrictedFeatureWrapper
                                                            automationType={
                                                                AutomationType.AutoCherryPickFromWorkingBranch
                                                            }
                                                            requiresTooltip
                                                        >
                                                            <Text>Commits to cherry-pick</Text>
                                                        </PlanRestrictedFeatureWrapper>
                                                    }
                                                    tooltip={{
                                                        title: `Selected fixes will be immediately cherry-picked onto ${
                                                            branchName ?
                                                                ` the '${branchName}' hotfix release branch`
                                                            :   ' the hotfix release branch.'
                                                        }`,
                                                        icon: <InfoCircleOutlined style={{ color: antGrey.grey7 }} />,
                                                    }}
                                                    name="commitHashes"
                                                    extra={
                                                        fetchCommitsError ?
                                                            <Text type="danger">Error fetching commits</Text>
                                                        :   <>
                                                                Latest commits on{' '}
                                                                {workingBranch ?
                                                                    <Text code>{workingBranch}</Text>
                                                                :   'working branch'}
                                                                {previousTag && (
                                                                    <>
                                                                        {' since last tag '}
                                                                        <Text code>{previousTag}</Text>
                                                                    </>
                                                                )}
                                                                {
                                                                    ". If you're looking for an older commit, search by commit hash instead."
                                                                }
                                                            </>
                                                    }
                                                >
                                                    <CommitSelector
                                                        allowSearch
                                                        searchValue={commitSearchValue}
                                                        onSearch={setCommitSearchValue}
                                                        loading={commitsLoading}
                                                        commits={commits}
                                                        style={{ width: '100%' }}
                                                        multiSelect={true}
                                                        disabled={isDisabled}
                                                    />
                                                </FormItem>
                                            )}
                                        </PlanRestrictedFeatureWrapper>
                                    )}
                                </div>
                            )}
                            {showRollbackOption && (
                                <>
                                    <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.Rollbacks}>
                                        {(isDisabled) => (
                                            <Radio value={CreateEditReleaseType.Rollback} disabled={isDisabled}>
                                                <span
                                                    style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                                                >
                                                    Create a hotfix release by rolling back to a previous stable build
                                                    <BetaBadge style={{ marginInlineEnd: 0 }} />
                                                    <PlanRestrictedFeatureWrapper
                                                        feature={SubscriptionFeatureType.Rollbacks}
                                                        requiresTooltip
                                                    />
                                                </span>
                                            </Radio>
                                        )}
                                    </PlanRestrictedFeatureWrapper>
                                    {isRollbackSelected && (
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 16,
                                                marginLeft: 20,
                                            }}
                                        >
                                            <FormItem
                                                label="Source version"
                                                name="sourceVersion"
                                                labelCol={{ flex: '140px' }}
                                                rules={[{ required: true, message: '' }]}
                                                style={{ marginBottom: 0 }}
                                            >
                                                <Select
                                                    options={possibleSourceVersionsForRollback.map(
                                                        (version, index) => ({
                                                            label: `${version}${index === 0 ? ' (latest stable, recommended)' : ''}`,
                                                            value: version,
                                                        }),
                                                    )}
                                                    placeholder="Select a source version"
                                                />
                                            </FormItem>
                                            <FormItem
                                                label="Strategy"
                                                name="resigningStrategy"
                                                labelCol={{ flex: '140px' }}
                                                initialValue={RollbackResigningStrategy.LocalResign}
                                                style={{ marginBottom: 0 }}
                                            >
                                                <Select style={{ width: '100%' }}>
                                                    {AllRollbackResigningStrategies.map((option) => (
                                                        <Select.Option value={option} key={option}>
                                                            {displayTextForResigningStrategy(option)}
                                                        </Select.Option>
                                                    ))}
                                                </Select>
                                            </FormItem>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </Radio.Group>
                )}

                {!isHotfixMode && (
                    <>
                        {scheduleIsActive && (
                            <>
                                <FormItem noStyle>
                                    <Text
                                        style={{
                                            ...titleStyle,
                                            fontWeight: 400,
                                            fontSize: 13,
                                            marginBottom: 0,
                                            display: 'block',
                                            color: runwayColors.black,
                                        }}
                                    >
                                        {isAppPlatform(appPlatform) && canConfigureReleaseDate ?
                                            'Set target kickoff/submit/release dates'
                                        : isAppPlatform(appPlatform) ?
                                            'Set target kickoff and submit dates'
                                        :   'Set target kickoff date'}
                                    </Text>
                                </FormItem>
                                <FormItem
                                    name="noTargetDates"
                                    label={
                                        <span style={{ color: antGrey.grey7, fontSize: 13 }}>
                                            If no target dates are set here, Runway will attempt to apply the schedule
                                            configured in App settings {'>'} Schedule.
                                        </span>
                                    }
                                    labelCol={{ span: 24 }}
                                />
                            </>
                        )}
                        <FormItem
                            label="Target kickoff date"
                            name="targetKickoffDate"
                            extra={
                                <span style={{ fontSize: 13 }}>
                                    {actualKickoffDate ?
                                        <>
                                            Your release was kicked off on{' '}
                                            <TimeWithConditionalTz
                                                style={{ fontSize: 13, color: 'inherit' }}
                                                triggeredAtLabel={formatDateWithTimezone(
                                                    actualKickoffDate,
                                                    'shortWithYearAndComma',
                                                    effectiveTimezone,
                                                    use24HourTimeFormat,
                                                )}
                                                triggeredByMoment={actualKickoffDate}
                                                effectiveTimezone={effectiveTimezone}
                                                use24HourTimeFormat={!!use24HourTimeFormat}
                                            />
                                        </>
                                    : release?.isStaticReleaseBranch || resultIsStaticBranch ?
                                        'Your release is kicked off when new code is detected past the last tag or promoted to the release branch by Runway.'
                                    :   'Your release is kicked off when a release branch is detected or created by Runway.'
                                    }
                                </span>
                            }
                        >
                            <DatePicker
                                needConfirm={false}
                                showTime={{
                                    defaultValue: dayjs('12:00', 'HH:mm'),
                                    showSecond: false,
                                    format: use24HourTimeFormat ? 'HH:mm' : 'h:mm A',
                                }}
                                showNow={false}
                                format={
                                    use24HourTimeFormat ?
                                        ['MM/DD [at] HH:mm [GMT]Z', 'MM/DD [at] HH:mm']
                                    :   ['MM/DD [at] h:mm A [GMT]Z', 'MM/DD [at] h:mm A']
                                }
                                minuteStep={5}
                            />
                        </FormItem>
                        {isAppPlatform(appPlatform) && (
                            <FormItem
                                label="Target submit date"
                                name="targetSubmitDate"
                                extra={
                                    actualSubmitDate ?
                                        <span style={{ fontSize: 13 }}>
                                            Your release was submitted on{' '}
                                            <TimeWithConditionalTz
                                                style={{ fontSize: 13, color: 'inherit' }}
                                                triggeredAtLabel={formatDateWithTimezone(
                                                    actualSubmitDate,
                                                    'shortWithYearAndComma',
                                                    effectiveTimezone,
                                                    use24HourTimeFormat,
                                                )}
                                                triggeredByMoment={actualSubmitDate}
                                                effectiveTimezone={effectiveTimezone}
                                                use24HourTimeFormat={!!use24HourTimeFormat}
                                            />
                                        </span>
                                    :   null
                                }
                            >
                                <DatePicker
                                    needConfirm={false}
                                    showTime={{
                                        defaultValue: dayjs('12:00', 'HH:mm'),
                                        showSecond: false,
                                        format: use24HourTimeFormat ? 'HH:mm' : 'h:mm A',
                                    }}
                                    showNow={false}
                                    minuteStep={5}
                                    format={
                                        use24HourTimeFormat ?
                                            ['MM/DD [at] HH:mm [GMT]Z', 'MM/DD [at] HH:mm']
                                        :   ['MM/DD [at] h:mm A [GMT]Z', 'MM/DD [at] h:mm A']
                                    }
                                />
                            </FormItem>
                        )}
                        {canConfigureReleaseDate && (
                            <FormItem
                                label="Target release date"
                                name="targetReleaseDate"
                                extra={
                                    actualReleasedDate ?
                                        <span style={{ fontSize: 13 }}>
                                            Your release was released on{' '}
                                            <TimeWithConditionalTz
                                                style={{ fontSize: 13, color: 'inherit' }}
                                                triggeredAtLabel={formatDateWithTimezone(
                                                    actualReleasedDate,
                                                    'shortWithYearAndComma',
                                                    effectiveTimezone,
                                                    use24HourTimeFormat,
                                                )}
                                                triggeredByMoment={actualReleasedDate}
                                                effectiveTimezone={effectiveTimezone}
                                                use24HourTimeFormat={!!use24HourTimeFormat}
                                            />
                                        </span>
                                    :   null
                                }
                            >
                                <DatePicker
                                    needConfirm={false}
                                    showTime={{
                                        defaultValue: dayjs('12:00', 'HH:mm'),
                                        showSecond: false,
                                        format: use24HourTimeFormat ? 'HH:mm' : 'h:mm A',
                                    }}
                                    minuteStep={5}
                                    showNow={false}
                                    format={
                                        use24HourTimeFormat ?
                                            ['MM/DD [at] HH:mm [GMT]Z', 'MM/DD [at] HH:mm']
                                        :   ['MM/DD [at] h:mm A [GMT]Z', 'MM/DD [at] h:mm A']
                                    }
                                />
                            </FormItem>
                        )}
                    </>
                )}

                {(isHotfixMode || props.isEditingRelease) && <Divider />}

                <MatchPhasedReleaseCheckbox
                    visible={shouldShowMatchPhasedReleaseOption}
                    defaultChecked={release?.shouldMatchPreviousPhasedRelease ?? false}
                    onChange={setShouldMatchPreviousPhasedRelease}
                    useFormItem={props.isEditingRelease}
                />
            </Form>
        </Modal>
    );
};
